import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, ChevronDown, Phone, User as UserIcon, LogOut, LayoutDashboard } from "lucide-react";
import { Logo } from "./Logo";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { useAuth } from "../context/AuthContext";

const initials = (name, email) =>
  (name || email || "U").split(" ").map(p => p[0]).slice(0, 2).join("").toUpperCase();

const AuthZone = () => {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  if (loading) return <div className="w-9 h-9 rounded-full bg-[#EAF5FD] animate-pulse" />;
  if (!user) {
    return (
      <Link to="/login">
        <Button data-testid="header-signin-btn" variant="outline" className="border-[#1088D4] text-[#1088D4] hover:bg-[#EAF5FD]">Sign in</Button>
      </Link>
    );
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid="header-avatar-btn" className="outline-none">
        <Avatar className="w-9 h-9 border border-[#E2E8F0] cursor-pointer">
          <AvatarImage src={user.picture} alt={user.name} />
          <AvatarFallback className="bg-[#273B59] text-white text-sm">{initials(user.name, user.email)}</AvatarFallback>
        </Avatar>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <div className="px-3 py-2">
          <p className="text-sm font-semibold text-[#273B59] truncate">{user.name || "OneLoan User"}</p>
          <p className="text-xs text-[#94A3B8] truncate">{user.email}</p>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="menu-profile" onClick={() => navigate("/profile")}><UserIcon size={15} className="mr-2" />My Profile</DropdownMenuItem>
        <DropdownMenuItem data-testid="menu-applications" onClick={() => navigate("/profile")}><LayoutDashboard size={15} className="mr-2" />My Applications</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="menu-logout" onClick={logout} className="text-red-600"><LogOut size={15} className="mr-2" />Logout</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const MobileAuthLink = ({ onNavigate }) => {
  const { user, logout } = useAuth();
  if (!user) {
    return <Link to="/login" onClick={onNavigate}><Button data-testid="mobile-signin-btn" className="w-full" variant="outline"><UserIcon size={15} className="mr-2" />Sign in</Button></Link>;
  }
  return (
    <div className="space-y-2">
      <Link to="/profile" onClick={onNavigate}><Button data-testid="mobile-profile-btn" className="w-full" variant="outline"><UserIcon size={15} className="mr-2" />My Profile</Button></Link>
      <Button data-testid="mobile-logout-btn" onClick={() => { logout(); onNavigate(); }} variant="ghost" className="w-full text-red-600"><LogOut size={15} className="mr-2" />Logout</Button>
    </div>
  );
};

const NAV = [
  { label: "Loans", children: [
    { label: "Personal Loan", to: "/personal-loan" },
    { label: "Business Loan", to: "/business-loan" },
    { label: "Flexi Overdraft Loan", to: "/overdraft-loan" },
  ]},
  { label: "Tools", children: [
    { label: "Compare Loans", to: "/tools/compare-loans" },
    { label: "EMI Calculator", to: "/tools/emi-calculator" },
    { label: "Eligibility Calculator", to: "/tools/eligibility-calculator" },
    { label: "Balance Transfer Calculator", to: "/tools/balance-transfer-calculator" },
    { label: "Part Payment Calculator", to: "/tools/part-payment-calculator" },
    { label: "Income Tax Calculator", to: "/tools/income-tax-calculator" },
    { label: "SIP Calculator", to: "/tools/sip-calculator" },
    { label: "FD / RD Calculator", to: "/tools/fd-rd-calculator" },
  ]},
  { label: "Learn", to: "/learn" },
  { label: "About Us", to: "/about" },
  { label: "Contact Us", to: "/contact" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(null);
  const loc = useLocation();

  return (
    <header data-testid="site-header" className="sticky top-0 z-50 backdrop-blur-xl bg-white/85 border-b border-[#E2E8F0]">
      <div className="ol-container flex items-center justify-between h-16 lg:h-20">
        <Logo />
        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((item) => (
            <div key={item.label} className="relative" onMouseEnter={() => setHovered(item.label)} onMouseLeave={() => setHovered(null)}>
              {item.children ? (
                <button data-testid={`nav-${item.label.toLowerCase()}`} className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-[#273B59] hover:text-[#1088D4] transition-colors">
                  {item.label} <ChevronDown size={14} />
                </button>
              ) : (
                <Link to={item.to} data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g,'-')}`} className={`px-4 py-2 text-sm font-medium transition-colors ${loc.pathname === item.to ? "text-[#1088D4]" : "text-[#273B59] hover:text-[#1088D4]"}`}>
                  {item.label}
                </Link>
              )}
              {item.children && hovered === item.label && (
                <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-[#E2E8F0] rounded-lg shadow-xl py-2 z-50">
                  {item.children.map(c => (
                    <Link key={c.to} to={c.to} className="block px-4 py-2 text-sm text-[#273B59] hover:bg-[#EAF5FD] hover:text-[#1088D4] transition-colors">
                      {c.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-2">
          <Link to="/apply?type=eligibility">
            <Button data-testid="header-eligibility-btn" variant="outline" className="border-[#1088D4] text-[#1088D4] hover:bg-[#EAF5FD]">Check Eligibility</Button>
          </Link>
          <Link to="/apply">
            <Button data-testid="header-apply-btn" className="ol-btn-primary">Apply Now</Button>
          </Link>
          <AuthZone />
        </div>

        {/* Mobile */}
        <div className="lg:hidden flex items-center gap-2">
          <Link to="/apply"><Button data-testid="header-apply-mobile" size="sm" className="ol-btn-primary">Apply</Button></Link>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild><Button data-testid="mobile-menu-btn" size="icon" variant="ghost"><Menu /></Button></SheetTrigger>
            <SheetContent side="right" className="w-[85%] sm:w-[400px] overflow-y-auto">
              <div className="mt-6">
                <Accordion type="single" collapsible>
                  {NAV.map((item, idx) => (
                    item.children ? (
                      <AccordionItem value={`item-${idx}`} key={item.label}>
                        <AccordionTrigger className="text-[#273B59] font-semibold">{item.label}</AccordionTrigger>
                        <AccordionContent>
                          <div className="flex flex-col gap-2 pl-2">
                            {item.children.map(c => (
                              <Link key={c.to} to={c.to} onClick={() => setOpen(false)} className="py-2 text-sm text-[#475569] hover:text-[#1088D4]">{c.label}</Link>
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ) : (
                      <Link key={item.label} to={item.to} onClick={() => setOpen(false)} className="block py-4 border-b border-[#E2E8F0] text-[#273B59] font-semibold">{item.label}</Link>
                    )
                  ))}
                </Accordion>
                <div className="mt-6 space-y-2">
                  <MobileAuthLink onNavigate={() => setOpen(false)} />
                  <Link to="/apply?type=eligibility" onClick={() => setOpen(false)}><Button className="w-full" variant="outline">Check Eligibility</Button></Link>
                  <Link to="/apply" onClick={() => setOpen(false)}><Button className="w-full ol-btn-primary">Apply Now</Button></Link>
                  <a href="tel:+919370251358" className="flex items-center justify-center gap-2 py-3 text-sm text-[#1088D4]"><Phone size={16}/> +91 93702 51358</a>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
